// 2024 Cover (SRD 5.2.1): Half Cover is +2 to AC and Dexterity saving throws, Three-Quarters Cover +5, and a target with
// Total Cover can't be targeted directly. Cover counts only when the attack comes from the other side of it, so an
// obstacle gives cover when the line from the attacker to the target passes through it. Behind several, only the most
// protective degree applies. Creatures, friend or foe, give Half Cover; walls and stamped objects come from a map's
// drawn layout (shared/mapLayout.mjs).
import { MAP_OBJECTS, STICKERS, WALL_KINDS, stickerFrame, stickerSquares } from '../mapLayout.mjs';

export const COVER_DEGREES = Object.freeze({
  half: Object.freeze({ degree: 'half', bonus: 2, label: 'Half Cover', rank: 1 }),
  'three-quarters': Object.freeze({ degree: 'three-quarters', bonus: 5, label: 'Three-Quarters Cover', rank: 2 }),
  total: Object.freeze({ degree: 'total', bonus: null, label: 'Total Cover', rank: 3 }),
});
export const HALF_COVER = Object.freeze({ degree: 'half', bonus: 2, label: 'Half Cover' });

/** The most protective of several covers (null for none). */
export const mostProtectiveCover = (...covers) => covers.filter(Boolean)
  .reduce((best, cover) => (!best || (COVER_DEGREES[cover.degree]?.rank || 0) > (COVER_DEGREES[best.degree]?.rank || 0) ? cover : best), null);

export const coverOf = (degree, source) => (COVER_DEGREES[degree]
  ? { degree, bonus: COVER_DEGREES[degree].bonus, label: COVER_DEGREES[degree].label, sources: [source] } : null);

// A line that only grazes the edge or corner of a space does not count as passing through it.
const EDGE_MARGIN = 0.1;

/** Whether the segment from `from` to `to` passes through the box (grid units), by the slab method. */
export const segmentCrossesBox = (from, to, box) => {
  let enter = 0;
  let exit = 1;
  for (const [start, end, low, high] of [[from.x, to.x, box.x0, box.x1], [from.y, to.y, box.y0, box.y1]]) {
    const delta = end - start;
    if (delta === 0) {
      if (start <= low || start >= high) return false;
      continue;
    }
    let near = (low - start) / delta;
    let far = (high - start) / delta;
    if (near > far) [near, far] = [far, near];
    enter = Math.max(enter, near);
    exit = Math.min(exit, far);
    if (enter >= exit) return false;
  }
  return true;
};

/**
 * Half Cover from creatures between an attacker and a target, or null. Each space is { x, y, squares }: its top-left
 * grid cell and its width in squares; `others` also carry an `id`. The result names the creatures in the way.
 */
export const creatureCover = ({ attacker, target, others = [] }) => {
  if (!attacker || !target) return null;
  const center = (space) => ({ x: space.x + space.squares / 2, y: space.y + space.squares / 2 });
  const from = center(attacker);
  const to = center(target);
  const blockerIds = others.filter((other) => other && segmentCrossesBox(from, to, {
    x0: other.x + EDGE_MARGIN, y0: other.y + EDGE_MARGIN, x1: other.x + other.squares - EDGE_MARGIN, y1: other.y + other.squares - EDGE_MARGIN,
  })).map((other) => other.id);
  return blockerIds.length ? { ...HALF_COVER, blockerIds } : null;
};

const cross = (a, b) => a.x * b.y - a.y * b.x;
const minus = (a, b) => ({ x: a.x - b.x, y: a.y - b.y });
const EPSILON = 1e-9;

/** Whether segment p1-p2 crosses segment q1-q2 strictly inside both (touching an end or running along it does not). */
export const segmentsCross = (p1, p2, q1, q2) => {
  const r = minus(p2, p1);
  const s = minus(q2, q1);
  const denominator = cross(r, s);
  if (Math.abs(denominator) < EPSILON) return false;
  const t = cross(minus(q1, p1), s) / denominator;
  const u = cross(minus(q1, p1), r) / denominator;
  return t > EPSILON && t < 1 - EPSILON && u > EPSILON && u < 1 - EPSILON;
};

const passesThroughPoint = (from, to, point) => {
  const line = minus(to, from);
  const offset = minus(point, from);
  if (Math.abs(cross(line, offset)) > 1e-6) return false;
  const along = (offset.x * line.x + offset.y * line.y) / (line.x * line.x + line.y * line.y);
  return along > EPSILON && along < 1 - EPSILON;
};

/**
 * Cover from a drawn layout between two points (grid units, usually square centers): wall edges the line crosses, grid
 * corners where two or more walls meet (so a diagonal can't slip through the joint of a continuous wall), and stamped
 * squares it passes through. `ignoreCells` are the attacker's and target's own squares. A sticker gives its cover when the
 * line passes through its turned outline, unless either end is on it: a creature standing on a sticker isn't behind it.
 *
 * `attackerCells` are the attacker's own squares. Cover pressed against them (a window or low wall on one of their
 * sides, a wall joint at one of their corners, a stamp or a sticker on a square next to them) is cover the attacker shoots over or
 * leans around, as from an arrow slit: it doesn't protect the target. It still protects the attacker from attacks the
 * other way. Total Cover always counts: nobody shoots through a solid wall. Returns the most protective cover, naming
 * what gives it, or null.
 */
/**
 * Whether a drawn layout's Heavily Obscured squares (fog, thick foliage) block sight between two creatures (2024: such
 * an area blocks sight entirely). Sight is blocked when either creature stands in one of those squares, or the line
 * between their centers passes through one. Returns { sources } naming what blocks it, or null when sight is clear.
 * `cells` are both creatures' own squares.
 */
export const layoutBlocksSight = ({ layout, from, to, cells = [] }) => {
  if (!layout || !from || !to) return null;
  const own = new Set(cells.map((cell) => `${cell.x},${cell.y}`));
  const sources = new Set();
  for (const [key, id] of Object.entries(layout.objects || {})) {
    const object = MAP_OBJECTS[id];
    if (object?.obscures !== 'heavy') continue;
    const [x, y] = key.split(',').map(Number);
    if (own.has(key) || segmentCrossesBox(from, to, { x0: x + EDGE_MARGIN, y0: y + EDGE_MARGIN, x1: x + 1 - EDGE_MARGIN, y1: y + 1 - EDGE_MARGIN })) {
      sources.add(object.label);
    }
  }
  return sources.size ? { sources: [...sources] } : null;
};

export const layoutCover = ({ layout, from, to, ignoreCells = [], attackerCells = [] }) => {
  if (!layout || !from || !to) return null;
  const own = new Set(attackerCells.map((cell) => `${cell.x},${cell.y}`));
  const isOwn = (x, y) => own.has(`${x},${y}`);
  const counts = (cover, againstAttacker) => Boolean(cover) && (cover.degree === 'total' || !againstAttacker);
  const covers = [];
  const cornerKinds = new Map();
  for (const [key, kind] of Object.entries(layout.walls || {})) {
    const wall = WALL_KINDS[kind];
    if (!wall?.cover) continue;
    const [xs, ys, dir] = key.split(',');
    const start = { x: Number(xs), y: Number(ys) };
    const end = dir === 'h' ? { x: start.x + 1, y: start.y } : { x: start.x, y: start.y + 1 };
    // The squares on either side of this edge.
    const againstAttacker = dir === 'h' ? isOwn(start.x, start.y - 1) || isOwn(start.x, start.y) : isOwn(start.x - 1, start.y) || isOwn(start.x, start.y);
    const cover = coverOf(wall.cover, wall.label);
    if (segmentsCross(from, to, start, end) && counts(cover, againstAttacker)) covers.push(cover);
    for (const corner of [start, end]) {
      const id = `${corner.x},${corner.y}`;
      cornerKinds.set(id, [...(cornerKinds.get(id) || []), kind]);
    }
  }
  for (const [id, kinds] of cornerKinds) {
    if (kinds.length < 2) continue;
    const [x, y] = id.split(',').map(Number);
    if (!passesThroughPoint(from, to, { x, y })) continue;
    const cover = mostProtectiveCover(...kinds.map((kind) => coverOf(WALL_KINDS[kind].cover, WALL_KINDS[kind].label)));
    // The four squares meeting at this corner.
    if (counts(cover, isOwn(x - 1, y - 1) || isOwn(x, y - 1) || isOwn(x - 1, y) || isOwn(x, y))) covers.push(cover);
  }
  const ignored = new Set(ignoreCells.map((cell) => `${cell.x},${cell.y}`));
  for (const [key, id] of Object.entries(layout.objects || {})) {
    const object = MAP_OBJECTS[id];
    if (!object?.cover || ignored.has(key)) continue;
    const [x, y] = key.split(',').map(Number);
    if (!segmentCrossesBox(from, to, { x0: x + EDGE_MARGIN, y0: y + EDGE_MARGIN, x1: x + 1 - EDGE_MARGIN, y1: y + 1 - EDGE_MARGIN })) continue;
    const cover = coverOf(object.cover, object.label);
    const nextToAttacker = attackerCells.some((cell) => Math.max(Math.abs(cell.x - x), Math.abs(cell.y - y)) <= 1);
    if (counts(cover, nextToAttacker)) covers.push(cover);
  }
  for (const sticker of layout.stickers || []) {
    const kind = STICKERS[sticker.kind];
    if (!kind?.cover) continue;
    const [start, end] = [stickerFrame(sticker, from), stickerFrame(sticker, to)];
    const outline = { x0: -kind.width / 2 + EDGE_MARGIN, x1: kind.width / 2 - EDGE_MARGIN, y0: -kind.length / 2 + EDGE_MARGIN, y1: kind.length / 2 - EDGE_MARGIN };
    const inside = ({ x, y }) => Math.abs(x) < kind.width / 2 && Math.abs(y) < kind.length / 2;
    if (inside(start) || inside(end) || !segmentCrossesBox(start, end, outline)) continue;
    const nextToAttacker = stickerSquares(sticker).some(({ x, y }) => attackerCells.some((cell) => Math.max(Math.abs(cell.x - x), Math.abs(cell.y - y)) <= 1));
    const cover = coverOf(kind.cover, kind.label);
    if (counts(cover, nextToAttacker)) covers.push(cover);
  }
  return mostProtectiveCover(...covers);
};
